"use client";

import { useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { FaLine } from "react-icons/fa6";
import { MdLogout } from "react-icons/md";
import { LoginImg } from "../assets";
import Image from "next/image";
import ConfirmPopUp from "./popup/confirmPopUp";
import type { AuthProvider, AuthStatus, AuthUser } from "../hooks/useAuth";
import { useTranslations } from "next-intl";

type LoginProps = {
    mode?: "inline" | "fullscreen";
    status: AuthStatus;
    user: AuthUser | null;
    onLogin?: (provider: AuthProvider) => void | Promise<void>;
    onLogout?: () => void | Promise<void>;
    onBackToMainLogin?: () => void;
};

export default function TeacherLogin({ mode = "inline", status, user, onLogin, onLogout, onBackToMainLogin }: LoginProps) {
    const displayUser = user;
    const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const tAuth = useTranslations("auth");

    const handleConfirmLogout = async () => {
        if (isLoggingOut) {
            return;
        }

        setIsLoggingOut(true);
        try {
            await onLogout?.();
            setIsLogoutConfirmOpen(false);
        } finally {
            setIsLoggingOut(false);
        }
    };

    if (mode === "fullscreen") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F4F3ED] p-3 sm:p-4 md:p-6 lg:p-0">
                <div className="flex w-full max-w-xl flex-col overflow-hidden overflow-y-auto rounded-2xl bg-white shadow-[0_8px_30px_rgb(0,0,0,0.08)] min-h-0 max-h-[calc(100dvh-1.5rem)] md:max-w-2xl lg:max-h-none lg:max-w-4xl lg:min-h-[700px] lg:flex-row lg:py-0">
                    <div className="relative hidden w-full lg:block lg:w-1/2">
                        <Image
                            src={LoginImg}
                            alt="THAI PILA Illustration"
                            fill
                            className="object-cover"
                            priority
                        />
                    </div>

                    <div className="flex w-full flex-col items-center justify-center px-5 py-6 sm:px-6 sm:py-7 md:px-8 md:py-8 lg:w-1/2 lg:p-4">
                        <h1 className="mb-6 text-xl font-bold text-gray-900 sm:mb-7 sm:text-2xl lg:mb-8">
                            Teacher Login
                        </h1>

                        <div className="flex w-full max-w-md flex-col sm:max-w-lg lg:max-w-none">
                            <div className="flex flex-col gap-3 sm:gap-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        void onLogin?.("google");
                                    }}
                                    className="flex w-full cursor-pointer items-center justify-start gap-3 rounded-md bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition hover:bg-neutral-50 sm:gap-4 sm:px-8 sm:py-3 lg:px-12"
                                >
                                    <FcGoogle size={24} />
                                    <span className="text-base font-medium sm:text-lg">เข้าสู่ระบบด้วย Google</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        void onLogin?.("line");
                                    }}
                                    className="flex w-full cursor-pointer items-center justify-start gap-3 rounded-md bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition hover:bg-neutral-50 sm:gap-4 sm:px-8 sm:py-3 lg:px-12"
                                >
                                    <FaLine size={24} color="#06C755" />
                                    <span className="text-base font-medium sm:text-lg">เข้าสู่ระบบด้วย Line</span>
                                </button>
                            </div>
                        </div>

                        <div className="pt-1 text-center text-black sm:pt-2">
                            <div className="mt-6 sm:mt-8 lg:mt-10">
                                <p className="mb-1 text-sm sm:text-base">
                                    สร้างบัญชีหรือเข้าสู่ระบบด้วยบริการที่คุณใช้ในโรงเรียนของคุณ
                                </p>
                                <p className="mb-1 text-sm sm:text-base">
                                    หากบริการที่คุณใช้ปกติไม่อยู่ในตัวเลือก
                                </p>
                                <p className="text-sm sm:text-base">กรุณาขอให้ครูของคุณติดต่อ edu.pila@oecd.org</p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onBackToMainLogin}
                            className="mt-6 w-fit cursor-pointer rounded-md bg-[#FCE654] px-5 py-2 text-sm font-medium text-gray-900 shadow-sm transition-all duration-200 ease-in-out hover:bg-[#F5DF4D] hover:text-black active:scale-[0.98] sm:mt-8 sm:px-6 lg:mt-8"
                        >
                            กลับไปหน้าเลือก Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (status === "checking") {
        return (
            <div className="rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-500">
                Loading account...
            </div>
        );
    }

    if (!displayUser) {
        return null;
    }

    return (
        <>
            <div className="flex items-center gap-2 rounded-md px-2 py-1">
                <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-rose-200 text-rose-700 font-bold">
                    {displayUser.picture ? (
                        <img
                            src={displayUser.picture}
                            alt={displayUser.name ?? "User avatar"}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <span>{(displayUser.name || displayUser.role || "A").charAt(0).toUpperCase()}</span>
                    )}
                </div>
                <span className="hidden max-w-42 truncate text-base sm:inline mr-4">
                    {displayUser.name ?? displayUser.role ?? displayUser.email ?? (displayUser.id ? `User ID: ${displayUser.id.substring(0, 8)}...` : "")}
                </span>
                <button
                    type="button"
                    onClick={() => setIsLogoutConfirmOpen(true)}
                    className="cursor-pointer text-neutral-700 hover:text-neutral-900"
                    aria-label="Sign out"
                    title="Sign out"
                >
                    <MdLogout size={20} />
                </button>
            </div>

            {isLogoutConfirmOpen ? (
                <ConfirmPopUp
                    title={tAuth("confirmLogout")}
                    message={tAuth("confirmLogoutMessage")}
                    confirmText={tAuth("confirm")}
                    cancelText={tAuth("cancel")}
                    onCancel={() => {
                        if (isLoggingOut) {
                            return;
                        }
                        setIsLogoutConfirmOpen(false);
                    }}
                    onConfirm={() => {
                        void handleConfirmLogout();
                    }}
                    isConfirming={isLoggingOut}
                />
            ) : null}
        </>
    );
}